<template>
    <div id="contentcontainter" class="standard-mode" role="main" aria-labelledby="login-title" style="display: flex; height: 95%; padding-top: 0px">
        <v-container id="login" fill-height tag="section" class="pa-0">
            <v-row justify="center" class="ma-auto">
                <v-slide-y-transition appear>
                    <v-card light max-width="100%" width="450" class="px-5 py-3 rounded_card quaroshadow mx-auto text-center" style="margin: auto">
                        <!-- Screenreader-H1 -->
                        <h1 id="login-title" class="sr-only">Login</h1>

                        <!-- Logo -->
                        <div id="sidebarLogo" :class="{ quaroshadow: true, whitebg: isOBI }" class="mx-auto">
                            <img width="150" id="whitelogo" :src="logoSrc" :alt="logoAlt" />
                        </div>

                        <v-card-text>
                            <!-- Formular -->
                            <form v-if="!twoFactorStep" @submit.prevent="login" novalidate aria-describedby="login-help" :aria-labelledby="'login-title'">
                                <p id="login-help" class="sr-only">Fields marked with * are required. Press Enter to submit.</p>

                                <!-- Email -->
                                <v-text-field
                                    id="login-email"
                                    rounded
                                    filled
                                    class="mr-3"
                                    v-model="email"
                                    :color="isOBI ? 'orange' : 'primary'"
                                    label="Email*"
                                    prepend-icon="mdi-email"
                                    @blur="checkForSso(false)"
                                    type="email"
                                    name="email"
                                    inputmode="email"
                                    autocomplete="email"
                                    :aria-required="true"
                                    required
                                    :aria-invalid="Boolean(emailError)"
                                    :aria-describedby="'login-help' + (emailError ? ' email-error' : '')"
                                    :error="Boolean(emailError)"
                                    :error-messages="emailError ? [emailError] : []" />

                                <!-- Passwort -->
                                <v-text-field
                                    v-if="!isOBI"
                                    id="login-password"
                                    rounded
                                    class="mr-3"
                                    filled
                                    v-model="pass"
                                    :color="isOBI ? 'orange' : 'primary'"
                                    label="Password*"
                                    prepend-icon="mdi-lock-outline"
                                    :type="show_pw ? 'text' : 'password'"
                                    name="password"
                                    autocomplete="current-password"
                                    :aria-required="true"
                                    required
                                    :aria-invalid="Boolean(passwordError)"
                                    :aria-describedby="'login-help' + (passwordError ? ' password-error' : '')"
                                    :error="Boolean(passwordError)"
                                    :error-messages="passwordError ? [passwordError] : []">
                                    <template v-slot:append>
                                        <v-btn
                                            icon
                                            :aria-label="show_pw ? 'Hide password' : 'Show password'"
                                            :aria-pressed="String(show_pw)"
                                            :aria-controls="'login-password'"
                                            @click="show_pw = !show_pw"
                                            type="button">
                                            <v-icon aria-hidden="true">{{ show_pw ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
                                        </v-btn>
                                    </template>
                                </v-text-field>

                                <!-- SSO / Login -->
                                <v-btn
                                    v-if="showSsoButton || isOBI"
                                    class="ml-5"
                                    :color="isOBI ? 'orange' : 'primary'"
                                    large
                                    rounded
                                    outlined
                                    @click="redirectToSSO"
                                    aria-label="Sign in with Single Sign-On"
                                    type="button">
                                    SSO
                                </v-btn>

                                <v-btn v-else :color="isOBI ? 'orange' : 'primary'" large rounded type="submit" :loading="loading" :aria-busy="String(loading)"> Login </v-btn>
                            </form>

                            <!-- 2FA-Code-Schritt -->
                            <form v-else @submit.prevent="verifyCode" novalidate aria-describedby="twofactor-help" :aria-labelledby="'login-title'">
                                <p id="twofactor-help" class="sr-only">Enter the 6-digit code we sent to your email. Press Enter to submit.</p>
                                <p role="status" aria-live="polite" class="mt-0 mb-6">We sent a verification code to {{ mailHint }}.</p>

                                <v-text-field
                                    data-focus-first
                                    id="twofactor-code"
                                    rounded
                                    filled
                                    class="mr-3 mb-4"
                                    hide-details="auto"
                                    v-model="code"
                                    :color="isOBI ? 'orange' : 'primary'"
                                    label="Verification code*"
                                    prepend-icon="mdi-shield-key-outline"
                                    inputmode="numeric"
                                    maxlength="6"
                                    name="one-time-code"
                                    autocomplete="one-time-code"
                                    :aria-required="true"
                                    required
                                    :aria-invalid="Boolean(twoFactorErrorText)"
                                    :aria-describedby="'twofactor-help' + (twoFactorErrorText ? ' twofactor-error' : '')"
                                    :error="Boolean(twoFactorErrorText)"
                                    :error-messages="twoFactorErrorText ? [twoFactorErrorText] : []" />

                                <v-checkbox v-model="rememberDevice" label="Remember this device for 7 days" hide-details class="mt-0 mb-4" />

                                <v-btn :color="isOBI ? 'orange' : 'primary'" large rounded type="submit" :loading="loading" :aria-busy="String(loading)"> Verify </v-btn>
                            </form>

                            <!-- 2FA: Resend / Back -->
                            <v-row v-if="twoFactorStep" no-gutters class="mt-4" align="center">
                                <v-col>
                                    <v-btn :color="isOBI ? 'orange' : 'primary'" text type="button" :disabled="resendCooldown > 0" @click="resendCode">
                                        {{ resendCooldown > 0 ? "Resend code (" + resendCooldown + "s)" : "Resend code" }}
                                    </v-btn>
                                    <v-spacer></v-spacer>
                                    <v-btn :color="isOBI ? 'orange' : 'primary'" text type="button" @click="backToLogin"> Back to login </v-btn>
                                </v-col>
                            </v-row>

                            <!-- Sign in with Google -->
                            <div v-if="!twoFactorStep && !isOBI && googleClientId" class="mt-5">
                                <div class="d-flex align-center login-divider mb-4" aria-hidden="true">
                                    <v-divider></v-divider>
                                    <span class="px-3 login-divider-text">or</span>
                                    <v-divider></v-divider>
                                </div>
                                <div class="d-flex justify-center">
                                    <GoogleLogin text="signin_with" @login-success="onOauthSuccess" @login-error="onOauthError"></GoogleLogin>
                                </div>
                            </div>

                            <!-- Register + Forgot Password -->
                            <v-row v-if="!twoFactorStep" no-gutters class="mt-5" align="center">
                                <v-col>
                                    <v-btn v-if="!isOBI" :color="isOBI ? 'orange' : 'primary'" class="mr-auto" @click="gotoRegister" text type="button"> Register </v-btn>

                                    <v-spacer></v-spacer>

                                    <!-- Forgot Password Dialog -->
                                    <v-dialog v-model="dialog" max-width="400px" aria-labelledby="reset-title" aria-modal="true">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn v-if="!isOBI" v-bind="attrs" v-on="on" :color="isOBI ? 'orange' : 'primary'" text type="button"> Forgot password? </v-btn>
                                        </template>

                                        <v-card id="passwordDialog" v-focus-trap="dialog">
                                            <v-card-title class="justify-center">
                                                <span id="reset-title" class="headline">Reset Password</span>
                                            </v-card-title>

                                            <v-card-text>
                                                <v-container>
                                                    <v-row>
                                                        <v-col cols="12" sm="12" md="12">
                                                            <v-text-field
                                                                data-focus-first
                                                                hide-details
                                                                rounded
                                                                filled
                                                                id="reset-email"
                                                                label="Email*"
                                                                v-model="email"
                                                                type="email"
                                                                name="reset-email"
                                                                autocomplete="email"
                                                                :aria-required="true"
                                                                required />
                                                        </v-col>
                                                    </v-row>
                                                </v-container>
                                            </v-card-text>
                                            <v-card-actions class="justify-center">
                                                <v-btn :color="isOBI ? 'orange' : 'primary'" rounded @click="checkEmail(email)" type="button"> Send Email </v-btn>
                                            </v-card-actions>
                                        </v-card>
                                    </v-dialog>
                                </v-col>
                            </v-row>

                            <!-- Resend verification -->
                            <v-dialog v-model="resendEmailDialog" v-if="showResendMessage" max-width="400px" aria-labelledby="resend-title" aria-modal="true">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn text v-bind="attrs" v-on="on" type="button"> Resend verification mail </v-btn>
                                </template>

                                <v-card id="passwordDialog" v-focus-trap="resendEmailDialog">
                                    <v-card-title class="justify-center">
                                        <span id="resend-title" class="headline">Resend verification mail</span>
                                    </v-card-title>

                                    <v-card-text>
                                        <v-container>
                                            <v-row>
                                                <v-col cols="12" sm="12" md="12">
                                                    <v-text-field
                                                        data-focus-first
                                                        id="resend-email"
                                                        label="Email*"
                                                        v-model="email"
                                                        type="email"
                                                        name="resend-email"
                                                        autocomplete="email"
                                                        :aria-required="true"
                                                        required />
                                                </v-col>
                                            </v-row>
                                        </v-container>
                                    </v-card-text>

                                    <v-card-actions class="justify-center">
                                        <v-btn :color="isOBI ? 'orange' : 'primary'" outlined @click="resendVerificationMail(email)" type="button"> Send Email </v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-dialog>

                            <!-- SNACKBARS / Live-Regions -->
                            <!-- Info neutral -->
                            <v-snackbar top :color="info.color" v-model="showInfo" ref="infoRef" role="status" aria-live="polite" timeout="8000" tabindex="-1">
                                <v-icon aria-hidden="true">{{ info.icon }}</v-icon>
                                <span class="font-weight-bold pl-2">{{ info.message }}</span>
                            </v-snackbar>

                            <!-- SSO Info -->
                            <v-snackbar top color="green" timeout="10000" v-model="showSSOInfo" ref="ssoInfoRef" role="status" aria-live="polite" tabindex="-1">
                                <v-icon aria-hidden="true">mdi-check-circle-outline</v-icon>
                                <span class="font-weight-bold pl-2"> SSO detected. Please use the SSO button to sign in. You will be redirected and logged in through your SSO provider. </span>
                            </v-snackbar>

                            <!-- Fehler: ungültige Kombination -->
                            <v-snackbar top v-model="authError" color="red" class="pl-10" ref="authErrorRef" role="alert" aria-live="assertive" timeout="10000" tabindex="-1">
                                <v-icon aria-hidden="true">mdi-alert-circle-outline</v-icon>
                                <span class="font-weight-bold"> The combination of the chosen username and password is invalid! </span>
                            </v-snackbar>

                            <!-- Fehler: kein User -->
                            <v-snackbar top v-model="showNoUserFound" color="red" class="pl-10" ref="noUserRef" role="alert" aria-live="assertive" timeout="10000" tabindex="-1">
                                <v-icon aria-hidden="true">mdi-alert-circle-outline</v-icon>
                                <span class="font-weight-bold"> We could not find the user you are looking for. Please contact the support at contact@quaro.io </span>
                            </v-snackbar>

                            <!-- Fehler: Verification fehlt -->
                            <v-snackbar top v-model="verificationError" color="red" class="pl-10" ref="verificationErrorRef" role="alert" aria-live="assertive" timeout="10000" tabindex="-1">
                                <v-icon aria-hidden="true">mdi-alert-circle-outline</v-icon>
                                <span class="font-weight-bold"> Your email address has not been verified yet! Please check your email account for our activation link. </span>
                            </v-snackbar>

                            <!-- Erfolg: verifiziert -->
                            <v-snackbar top v-model="showSuccessMessage" color="green" class="pl-10" ref="successRef" role="status" aria-live="polite" timeout="8000" tabindex="-1">
                                <v-icon aria-hidden="true">mdi-check-circle-outline</v-icon>
                                <span class="font-weight-bold"> Your email address has been verified! </span>
                            </v-snackbar>

                            <!-- Erfolg: Reset-Mail gesendet -->
                            <v-snackbar top v-model="showResetPasswordMessage" color="green" class="pl-10" ref="resetPwRef" role="status" aria-live="polite" timeout="10000" tabindex="-1">
                                <v-icon aria-hidden="true">mdi-check-circle-outline</v-icon>
                                <span class="font-weight-bold"> We have sent you a link to reset your password! Please check your email account. </span>
                            </v-snackbar>
                        </v-card-text>
                    </v-card>
                </v-slide-y-transition>
            </v-row>
        </v-container>

        <Datenschutz :show="showDatenschutz" @close="showDatenschutz = false"></Datenschutz>

        <v-footer padless fixed bottom>
            <v-col class="footer-col d-flex">
                <v-btn text class="mx-auto" small href="https://quaro.io/agb/" target="_blank" rel="noopener noreferrer" aria-label="Open AGB in new tab"> AGB </v-btn>
            </v-col>
            <v-col class="footer-col d-flex">
                <v-btn text class="mx-auto" small href="https://quaro.io/impressum/" target="_blank" rel="noopener noreferrer" aria-label="Open Impressum in new tab"> Impressum </v-btn>
            </v-col>
            <v-col class="footer-col d-flex">
                <v-btn text class="mx-auto" small @click="showDatenschutz = true" aria-controls="Open data protection dialog" aria-expanded="false"> Datenschutz </v-btn>
            </v-col>
        </v-footer>
    </div>
</template>

<script>
    import Datenschutz from "../components/footer/Datenschutz.vue"
    import GoogleLogin from "../components/GoogleLogin.vue"
    import auth from "../components/helper/auth"

    export default {
        name: "Login",
        components: { Datenschutz, GoogleLogin },
        data: () => ({
            loading: false,
            showAGB: false,
            showDatenschutz: false,
            auth: "Basic " + window.btoa(process.env.VUE_APP_AUTH),
            email: "",
            pass: "",
            show_pw: false,
            dialog: false,
            resendEmailDialog: false,
            authError: false,
            verificationError: false,
            showSuccessMessage: false,
            showSsoButton: false,
            showSSOInfo: false,
            showNoUserFound: false,
            showResetPasswordMessage: false,
            showResendMessage: false,
            ssoChecked: false,
            // 2FA-Code-Schritt (siehe auth.js login()/verifyTwoFactor())
            twoFactorStep: false,
            challengeId: null,
            mailHint: "",
            code: "",
            rememberDevice: false,
            twoFactorErrorText: null,
            resendCooldown: 0,
            resendTimer: null,
            info: {
                message: null,
                icon: null,
                color: null
            },
            showInfo: false,
            socials: [
                { href: "https://www.facebook.com/quaro.io", icon: "mdi-facebook" },
                { href: "https://www.instagram.com/quaro.io/", icon: "mdi-instagram" },
                { href: "https://de.linkedin.com/company/quaro-das-keyword-tool", icon: "mdi-linkedin" }
            ]
        }),
        computed: {
            isOBI() {
                return window.location.href.includes("apollon.obi.com")
            },
            logoSrc() {
                return this.isOBI ? require("@/assets/apollon_logo.png") : require("@/assets/quaro_final_white.svg")
            },
            logoAlt() {
                return this.isOBI ? "Apollon logo" : "Quaro logo"
            },
            googleClientId() {
                return process.env.VUE_APP_GOOGLE_CLIENT_ID || ""
            }
        },
        methods: {
            focusSnackbar(refName) {
                this.$nextTick(() => {
                    const el = this.$refs[refName]?.$el
                    if (el && typeof el.focus === "function") el.focus()
                })
            },
            openAGB() {
                window.open(`https://quaro.io/agb/`, "_blank")
            },
            openImprint() {
                window.open(`https://quaro.io/impressum/`, "_blank")
            },
            createInfo(message, color, icon) {
                this.info.message = message
                this.info.color = color
                this.info.icon = icon
                this.showInfo = true
                this.focusSnackbar("infoRef")
            },
            resendVerificationMail(email) {
                if (email.length > 0) {
                    this.$http
                        .post(process.env.VUE_APP_APIURL + "/user/verificate/mail", { email: email }, { headers: { Authorization: this.auth } })
                        .then((response) => {
                            if (response.status == 200) {
                                this.resendEmailDialog = false
                                this.createInfo("Email has been resent. Please check your email account for our activation-link!", "green", "mdi-check-circle-outline")
                            } else {
                                this.showNoUserFound = true
                                this.focusSnackbar("noUserRef")
                            }
                        })
                        .catch(() => {
                            this.showNoUserFound = true
                            this.focusSnackbar("noUserRef")
                        })
                }
            },
            checkEmail(email) {
                if (email.length > 0) {
                    this.$http
                        .post(process.env.VUE_APP_APIURL + "/user/resetpassword", { email: email }, { headers: { Authorization: this.auth } })
                        .then((response) => {
                            if (response.body.user_found) {
                                this.showResetPasswordMessage = true
                                this.dialog = false
                                this.focusSnackbar("resetPwRef")
                                try {
                                    this.$matomo.trackEvent("Login", "resetPassword", email, "")
                                } catch (error) {
                                    // matomo blocked
                                }
                            } else {
                                this.showNoUserFound = true
                                this.focusSnackbar("noUserRef")
                                try {
                                    this.$matomo.trackEvent("Login", "resetPassword - no user found ", email, "")
                                } catch (error) {
                                    // matomo blocked
                                }
                            }
                        })
                        .catch(() => {
                            // optional technical error handling
                        })
                }
            },
            gotoRegister() {
                try {
                    this.$matomo.trackEvent("Login", "gotoRegister", "", "")
                } catch (error) {}
                this.$router.push({ path: "/register" })
            },
            redirectToSSO() {
                window.location = process.env.VUE_APP_SAMLURL + "/" + (this.isOBI ? "obi.de" : this.email.split("@")[1])
            },
            checkForSso(loginAfterChecking) {
                if (this.email.length > 0) {
                    this.ssoChecked = false
                    this.$http
                        .post(process.env.VUE_APP_APIURL + "/user/checkforsso", { mail: this.email }, { headers: { Authorization: this.auth } })
                        .then((response) => {
                            if (response.body.sso_status == 1) {
                                this.showSSOInfo = true
                                this.showSsoButton = true
                                this.ssoChecked = true
                                this.focusSnackbar("ssoInfoRef")
                            } else {
                                this.ssoChecked = true
                                this.showSsoButton = false
                                if (loginAfterChecking) {
                                    this.login()
                                }
                            }
                        })
                        .catch(() => {
                            this.ssoChecked = true
                            this.showSsoButton = false
                            if (loginAfterChecking) {
                                this.login()
                            }
                        })
                }
            },
            login() {
                this.loading = true
                if (this.ssoChecked) {
                    try {
                        this.$matomo.trackEvent("Login", "login", "", "")
                    } catch (error) {}
                    auth.login(this.email, this.pass, "quaro", null, (loggedIn) => {
                        if (loggedIn.userfound) {
                            if (loggedIn.active) {
                                if (loggedIn.twofactor_required) {
                                    this.challengeId = loggedIn.challenge_id
                                    this.mailHint = loggedIn.mail_hint
                                    this.twoFactorStep = true
                                    this.loading = false
                                } else if (loggedIn.verification) {
                                    if (loggedIn.authentication) {
                                        this.$store.dispatch("FETCH_STARTUP_DATA", this.email)
                                        this.$router.replace(this.$route.query.redirect || "/")
                                    } else {
                                        this.authError = true
                                        this.loading = false
                                        this.focusSnackbar("authErrorRef")
                                    }
                                } else {
                                    this.verificationError = true
                                    this.showResendMessage = true
                                    this.loading = false
                                    this.focusSnackbar("verificationErrorRef")
                                }
                            } else {
                                this.createInfo("Your account expired. Please contact contact@quaro.io for further assistance.", "#fcba03", "mdi-alert-circle-outline")
                                this.loading = false
                            }
                        } else {
                            this.createInfo("We could not find a account for this email, please register first.", "#fcba03", "mdi-alert-circle-outline")
                            this.loading = false
                        }
                    })
                } else {
                    this.loading = false
                    this.checkForSso(true)
                }
            },
            verifyCode() {
                this.loading = true
                this.twoFactorErrorText = null
                auth.verifyTwoFactor(this.challengeId, this.code, this.rememberDevice, (result) => {
                    if (result.authentication) {
                        this.$store.dispatch("FETCH_STARTUP_DATA", this.email)
                        this.$router.replace(this.$route.query.redirect || "/")
                    } else {
                        this.loading = false
                        this.twoFactorErrorText = this.twoFactorErrorMessage(result.twofactor_error, result.attempts_left)
                    }
                })
            },
            twoFactorErrorMessage(error, attemptsLeft) {
                if (error == "wrong_code") return "Incorrect code." + (attemptsLeft != null ? " (" + attemptsLeft + " attempts left)" : "")
                if (error == "expired") return "This code has expired. Please request a new one."
                if (error == "too_many_attempts") return "Too many attempts. Please request a new code."
                if (error == "invalid_challenge") return "This code is no longer valid. Please log in again."
                return "Something went wrong. Please try again."
            },
            resendCode() {
                if (this.resendCooldown > 0) return
                auth.resendTwoFactor(this.challengeId, (result) => {
                    if (result.sent) {
                        this.startResendCooldown(60)
                        this.createInfo("A new code has been sent.", "green", "mdi-check-circle-outline")
                    } else if (result.retry_after) {
                        this.startResendCooldown(result.retry_after)
                    } else {
                        this.createInfo("We could not resend the code. Please try again later.", "#fcba03", "mdi-alert-circle-outline")
                    }
                })
            },
            startResendCooldown(seconds) {
                this.resendCooldown = seconds
                clearInterval(this.resendTimer)
                this.resendTimer = setInterval(() => {
                    this.resendCooldown--
                    if (this.resendCooldown <= 0) clearInterval(this.resendTimer)
                }, 1000)
            },
            backToLogin() {
                this.twoFactorStep = false
                this.code = ""
                this.twoFactorErrorText = null
                clearInterval(this.resendTimer)
                this.resendCooldown = 0
            },
            onOauthSuccess(mail) {
                // mail kommt AES-verschluesselt aus dem Login-Payload (wie localStorage.user) -
                // FETCH_STARTUP_DATA braucht die Klartext-Adresse (vgl. App.vue mounted()).
                const plainMail = this.CryptoJS.AES.decrypt(mail, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                this.$store.dispatch("FETCH_STARTUP_DATA", plainMail)
                this.$router.replace(this.$route.query.redirect || "/")
            },
            onOauthError(error) {
                if (error && error.singleusererror) {
                    this.createInfo("This company only allows a single user account.", "#fcba03", "mdi-alert-circle-outline")
                } else if (error && error.oauth_error == "email_not_verified") {
                    this.createInfo("Your Google account's email address is not verified.", "#fcba03", "mdi-alert-circle-outline")
                } else if (error && error.oauth_error == "provider_not_configured") {
                    this.createInfo("Google sign-in is not available right now.", "#fcba03", "mdi-alert-circle-outline")
                } else {
                    this.createInfo("Google sign-in failed. Please try again.", "#fcba03", "mdi-alert-circle-outline")
                }
            },
            verificateUser() {
                this.$http
                    .get(process.env.VUE_APP_APIURL + "/user/auth/" + this.$route.params.auth, { headers: { Authorization: this.auth } })
                    .then((response) => {
                        if (response.body.success) {
                            this.showSuccessMessage = true
                            this.focusSnackbar("successRef")
                            try {
                                this.$matomo.trackEvent("Login", "verificateUser - verficated ", this.$route.params.auth, "")
                            } catch (error) {}
                        } else {
                            this.showNoUserFound = true
                            this.focusSnackbar("noUserRef")
                            try {
                                this.$matomo.trackEvent("Login", "verificateUser - not verficated ", this.$route.params.auth, "")
                            } catch (error) {}
                        }
                    })
                    .catch(() => {
                        // optional technical error handling
                    })
            }
        },
        mounted() {
            if (this.$route.params.auth) {
                this.verificateUser()
            }
        },
        beforeDestroy() {
            clearInterval(this.resendTimer)
        }
    }
</script>

<style lang="scss" scoped>
    #passwordDialog {
        box-shadow: none !important;
    }
    #whitelogo {
        padding-top: 22%;
    }
    .whitebg {
        padding-top: 2%;
        background-color: white !important;
        background: linear-gradient(56deg, #c4c4c4 0%, #c2c2c2 59%, #a0a0a0 100%) !important;
    }
    #sidebarLogo {
        width: 300px;
        height: 170px;
        margin-top: -70px;
        border-radius: 12px;
        margin-bottom: 12px;
        background: linear-gradient(56deg, #019e86 0%, #009e85 59%, #0c8574 100%);
    }
    .registerLink {
        color: rgb(185, 185, 185);
        font-size: 0.9em;
        margin-bottom: -20px;
        padding-top: 25px;
        margin: 0%;
    }
    .registerLink:hover {
        cursor: pointer;
        color: #1cedb2;
    }
    .footer-col:hover {
        background-color: rgb(236, 236, 236);
        cursor: pointer;
    }
    .footer-col {
        display: flex;
        font-size: 0.8em;
        span {
            margin-left: auto;
            margin-right: auto;
        }
    }
    .forgotpwlink {
        color: rgb(185, 185, 185);
        font-size: 0.9em;
        margin-bottom: -20px;
        margin: 0%;
    }
    .forgotpwlink:hover {
        cursor: pointer;
        color: #1cedb2;
    }
    .login-divider {
        max-width: 320px;
        margin-left: auto;
        margin-right: auto;
    }
    .login-divider-text {
        color: rgb(150, 150, 150);
        font-size: 0.75em;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        white-space: nowrap;
    }
    .welcome {
        margin-top: -50px;
    }
    .text-center {
        padding-bottom: 0px;
    }
    #sendmailbtn {
        margin-right: 50%;
    }
    /* Screenreader-only helper for BITV */
    .sr-only {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
    }
</style>
